import { get, invert, keyBy } from 'lodash';
import { matchPath } from 'react-router-dom';
import type { ParametricSelector, Selector } from 'reselect';
import { filter } from 'rxjs/operators';
import UrlParse from 'url-parse';

import type { Brand, RoutingKey } from '@swf/core/enums';
import { Vertical } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Layout } from '@swf/core/types/Layout';
import { canUseDOM } from '@swf/core/utils';
import { flattenWidgets } from '@swf/core/utils/flattenWidgets';
import { formatOptionalParams } from '@swf/core/utils/paths';
import deriveMeetingBasedEventTypeId, {
  isAnimalRacingRoutingKey
} from '@swf/sports/utils/getMeetingBasedEventTypeId';
import * as urlMarketId from '@swf/sports/utils/urlMarketId';

import type { WidgetContainer } from '../types/Widget';
import { stripLocale } from '../utils/stripLocale';
import { fetchProperties } from '../utils/widgets';

import type { LocaleState } from './locale';
import { defaultModel } from './widget';

export interface PageMeta {
  PWATitle: string;
  activePath: string;
  authRequired?: boolean;
  baseURL: string;
  description: string;
  noIndex?: boolean;
  favicon?: string;
  redirect?: { statusCode?: number; location: string };
  routes: { [key: string]: string };
  source?: string;
  title: string;
  titleTemplate?: string;
  tokens: Record<LocaleState, Record<string, unknown>>;
  trackingId: string;
  vertical: Vertical;
  brand?: Brand;
}

export interface PageState {
  layout?: Layout;
  meta?: PageMeta;
  url?: UrlParse;
}

interface ThisType {
  getMeta: Selector<RootState, Partial<PageMeta>>;
  getActiveRoutingKey: Selector<RootState, string | undefined>;
  matchRoute: ParametricSelector<RootState, RoutingKey, boolean>;
  getVertical: Selector<RootState, Vertical>;
  getLayout: Selector<RootState, Layout | undefined>;
  getUrl: Selector<RootState, UrlParse | undefined>;
}

interface PageModel extends ModelConfig<PageState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

interface RequestBody {
  path: string;
  siteId?: string;
}

export type PageChangeAction = PageState & { widgetsToLoad?: WidgetContainer[] };

const templateCache = new Map<string, PageState>();

const reducers = {
  _change: (_state: PageState, { widgetsToLoad, ...payload }: PageChangeAction = {}) => payload,

  // This is facade action that allows models to listen to page ready state.
  ready: (state: PageState) => state
};

const effects = {
  async prepareWidget(widget: WidgetContainer, { actions, addModel }: EffectParams) {
    const { model = defaultModel } = await fetchProperties(widget.type);
    const state = get(canUseDOM && window, ['__INITIAL_STATE__', widget.id], model.state);
    const unmount = actions.pipe(
      filter(
        ({ payload, type }) =>
          type === 'page/_change' &&
          (!payload.layout || !flattenWidgets(payload.layout).find((item) => item.id === widget.id))
      )
    );

    addModel({ ...model, state, name: widget.id, widget: { ...widget, unmount } });
    if (canUseDOM && window.__INITIAL_STATE__) delete window.__INITIAL_STATE__[widget.id];
  },

  async loadWidget(widget: WidgetContainer, { dispatch }: EffectParams) {
    const preload = dispatch[widget.id].preload?.().catch(() => undefined);
    if (!canUseDOM) await preload;
  },

  /**
   * Runs before the new page is added to the store. Adds widget models to store.
   */
  async prepare({ widgetsToLoad, layout }: PageChangeAction = {}, params: EffectParams) {
    const widgets = widgetsToLoad || params.select.page.getWidgetsToLoad(params.rootState, layout);
    await Promise.all(widgets.map(async (widget) => effects.prepareWidget(widget, params)));
  },

  /**
   * Runs after the new page is added to the store. Fires widget load events.
   */
  async change(action: PageChangeAction = {}, params: EffectParams) {
    const { rootState, dispatch, select } = params;
    const currentLayout = select.page.getLayout(rootState);
    if (action?.meta?.redirect && currentLayout) {
      action.layout = currentLayout;
    }
    if ('url' in action) {
      const locale = select.locale.get(rootState);
      action.url?.set('pathname', stripLocale(action.url.pathname, locale));
    }

    // eslint-disable-next-line no-underscore-dangle
    await dispatch.page._change(action);
  },

  async _change({ layout, meta, widgetsToLoad }: PageChangeAction = {}, params: EffectParams) {
    if (!layout || meta?.redirect?.location) return;

    const widgets = widgetsToLoad || flattenWidgets(layout);
    await Promise.all(widgets.map(async (widget) => effects.loadWidget(widget, params)));
  },

  /**
   * Runs on client ready. Ensures the widget models are prepared and fires widget load events.
   */
  async init(_payload: undefined, params: EffectParams) {
    const layout = params.select.page.getLayout(params.rootState);

    if (!layout) return;

    const widgets = flattenWidgets(layout);
    await Promise.all(
      widgets.map(async (widget) => {
        await effects.prepareWidget(widget, params);
        await effects.loadWidget(widget, params);
      })
    );

    await params.dispatch.page.ready();
  },

  async fetch({ href, version }: { href: string; version?: string }, params: EffectParams) {
    const { rootState, dispatch, request } = params;
    const location = select.location.get(rootState);
    const url = new UrlParse(href, true);
    const { pathname, query } = url;
    const siteId = query.siteId || location.query.siteId;

    const path = select.location.stripLocale(rootState, pathname);
    const payload = {
      service: 'cms.template',
      body: {
        path,
        clientVersion: canUseDOM ? window.version : version
      }
    };

    if (siteId) {
      (payload.body as RequestBody).siteId = siteId;
      url.set('query', { ...query, siteId });
    }

    const cacheKey = url.toString();
    let template: Omit<PageState, 'url'> | undefined;

    if (canUseDOM) {
      template = templateCache.get(cacheKey);
    }

    if (!template) {
      const { body } = await request<Omit<PageState, 'url'>>(payload);
      template = body;
    }

    if (canUseDOM) {
      templateCache.set(cacheKey, template);
    }

    const widgetsToLoad = select.page.getWidgetsToLoad(rootState, template.layout);
    await dispatch.page.prepare({ widgetsToLoad, ...template });
    await dispatch.page.change({ widgetsToLoad, url, ...template });
  }
};

const selectors = (slice: Slicer<PageState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getActiveRoutingKey() {
    return slice(({ meta }) => {
      if (meta?.routes) {
        const match = Object.entries(meta.routes).find(
          // format URL params between Yoda and Routes
          ([_key, path]) => formatOptionalParams(path) === formatOptionalParams(meta.activePath)
        );
        return match && match[0];
      }
    });
  },
  getVertical() {
    return slice((state) => state.meta?.vertical || Vertical.Global);
  },
  getVerticalName(this: ThisType) {
    return createSelector(this.getVertical, (vertical) => invert(Vertical)[vertical]);
  },
  getMeta() {
    return slice((state) => (state.meta || {}) as Partial<PageMeta>);
  },
  getUrl() {
    return slice((state) => state.url);
  },
  getFavicon() {
    return slice((state) => state.meta?.favicon);
  },
  getLayout() {
    return slice((state) => state?.layout);
  },
  getWidget() {
    return createSelector(
      slice,
      (_rootState: RootState, id: string | undefined) => id,
      ({ layout }, id) =>
        layout
          ? flattenWidgets(layout).find((widget) => widget.id === id || widget.type === id)
          : undefined
    );
  },
  matchRoute(this: { getMeta: Selector<RootState, Partial<PageMeta>> }) {
    return createSelector(
      (rootState) => select.location.stripLocale(rootState, undefined),
      this.getMeta,
      (_rootState: RootState, key: RoutingKey) => key,
      (pathname, { routes }, key) =>
        !!(routes && matchPath(pathname, { path: routes[key], exact: true, strict: false }))
    );
  },
  getMatchingRoute(this: ThisType) {
    return createSelector(
      (rootState: RootState, routingKeys: RoutingKey[]) => ({ rootState, routingKeys }),
      ({ rootState, routingKeys }) => routingKeys.find((key) => this.matchRoute(rootState, key))
    );
  },
  isAuthRequired() {
    return slice((state) => !!state.meta?.authRequired);
  },
  isSportsVertical() {
    return slice((state) => state.meta?.vertical === Vertical.Sports);
  },
  isStatic() {
    return slice((state) => state.meta?.source === 'cms');
  },
  getUrlParams(this: ThisType) {
    return createSelector(
      this.getUrl,
      (rootState) => select.location.isYoda(rootState),
      this.getMeta,
      this.getActiveRoutingKey,
      (url, isYoda, meta, activeKey): Record<string, string> => {
        if (isYoda || !url || !activeKey) return {};

        const route = matchPath(url.pathname, {
          path: meta.routes?.[activeKey],
          exact: true,
          strict: false
        });

        return route?.params || {};
      }
    );
  },
  getSportsUrlParams(this: {
    getUrlParams: Selector<RootState, Record<string, string>>;
    getActiveRoutingKey: Selector<RootState, string | undefined>;
  }) {
    return createSelector(
      this.getUrlParams,
      this.getActiveRoutingKey,
      (urlParams, activeKey): Record<string, string> => {
        const { marketId } = urlParams;

        const meetingBasedEventTypeId = isAnimalRacingRoutingKey(activeKey)
          ? String(deriveMeetingBasedEventTypeId(activeKey))
          : undefined;

        return {
          ...urlParams,
          ...(meetingBasedEventTypeId && { eventTypeId: meetingBasedEventTypeId }),
          ...(marketId && { marketId: urlMarketId.parse(marketId) })
        };
      }
    );
  },
  getWidgetsToLoad(this: ThisType) {
    return createSelector(
      this.getLayout,
      (_rootState: RootState, newLayout: Layout | undefined) => newLayout,
      (currentLayout, newLayout) => {
        if (currentLayout && newLayout) {
          const currentWidgets = keyBy(flattenWidgets(currentLayout), 'id');
          return flattenWidgets(newLayout).filter(({ id }) => !currentWidgets[id]);
        }

        if (newLayout) {
          return flattenWidgets(newLayout);
        }

        if (currentLayout) {
          return flattenWidgets(currentLayout);
        }

        return [];
      }
    );
  }
});

const page: PageModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default page;
